import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import Scrollspy from "react-scrollspy";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import shell from "react-syntax-highlighter/dist/esm/languages/hljs/shell";
import xml from "react-syntax-highlighter/dist/esm/languages/hljs/xml";
import json from "react-syntax-highlighter/dist/esm/languages/hljs/json";
import github from "react-syntax-highlighter/dist/esm/styles/hljs/github";
import AudioPlayer from "./AudioPlayer";
import Alert from "./presentational/Alert";
import Spinner from "./presentational/Spinner";
import SPEAKERS_LIST from "../speakers";
import MP3SpeakTag from "../assets/audio/ssml/speak.mp3";
import MP3VoicerySpeakerTag from "../assets/audio/ssml/voicery-speaker.mp3";
import MP3VoiceryStyleTag from "../assets/audio/ssml/voicery-style.mp3";
import MP3PSTag from "../assets/audio/ssml/p-s.mp3";
import MP3WTokenTag from "../assets/audio/ssml/w-token.mp3";
import MP3SayAsTag from "../assets/audio/ssml/say-as.mp3";
import MP3BreakTag from "../assets/audio/ssml/break.mp3";
import MP3EmphasisTag from "../assets/audio/ssml/emphasis.mp3";
import MP3SubTag from "../assets/audio/ssml/sub.mp3";
import MP3ProsodyTag from "../assets/audio/ssml/prosody.mp3";
import MP3PhonemeTag from "../assets/audio/ssml/phoneme.mp3";
import MP3AudioTag from "../assets/audio/ssml/audio.mp3";
SyntaxHighlighter.registerLanguage("shell", shell);
SyntaxHighlighter.registerLanguage("xml", xml);
SyntaxHighlighter.registerLanguage("json", json);
const getBaseUrl = () => {
    return window.location.hostname === "localhost"
        ? `${window.location.origin}/api`
        : window.location.origin.replace("www.", "api.");
};
const Attribute = ({ name, type, required = false, showRequirement = true, defaultValue, children }) => {
    const requirement = required ? (React.createElement("span", { className: "doc_red" }, "required")) : (React.createElement("span", { className: "doc_green" }, "optional"));
    return (React.createElement("div", null,
        React.createElement("b", { className: "doc_blue" }, name),
        React.createElement("span", { className: "doc_code" }, type),
        showRequirement && requirement,
        " ",
        defaultValue && (React.createElement("span", { className: "doc_green" },
            "(default: ",
            React.createElement("b", null, defaultValue),
            ")")),
        React.createElement("br", null),
        children));
};
const Attributes = ({ title = "Attributes", children }) => {
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "doc_sub-title" },
            React.createElement("h4", null, title)),
        React.createElement("div", { className: "doc_info" }, children)));
};
const ExampleUsage = ({ title = "Example Usage", language = "xml", audio, children }) => {
    if (typeof children !== "string") {
        throw new Error(`ExampleUsage must contain only string children, not ${typeof children} children.`);
    }
    const rawLines = children.match(/[^\r\n]+/g);
    if (!rawLines) {
        throw new Error("ExampleUsage must contain non-empty string.");
    }
    const lines = rawLines.filter((line, idx) => line.trim().length !== 0 && (idx !== 0 || idx !== rawLines.length - 1));
    const trimLeftAmount = Math.min(...lines.map(line => line.length - line.trimLeft().length));
    const ssml = lines.map(line => line.substr(trimLeftAmount)).join("\n");
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "doc_sub-title" },
            React.createElement("h4", null, title)),
        React.createElement("div", { className: "doc_code-blocks" },
            React.createElement(SyntaxHighlighter, { language: language, style: github }, ssml)),
        audio && React.createElement(AudioPlayer, { seekbar: true, src: audio })));
};
const DocsNav = () => {
    useEffect(() => {
        const centerSideNavScroll = () => {
            const docMenu = document.querySelector(".doc-menu-fix");
            if (!docMenu) {
                return;
            }
            const activeItem = docMenu.querySelector(".active");
            const subActiveItem = activeItem
                ? activeItem.querySelector(".sub-active")
                : null;
            const focusedItem = subActiveItem || activeItem;
            if (focusedItem) {
                focusedItem.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }
        };
        const updateSideNavScroll = () => {
            clearTimeout(window.docsSideNavScrollTimer);
            window.docsSideNavScrollTimer = window.setTimeout(centerSideNavScroll, 250);
        };
        window.addEventListener("scroll", updateSideNavScroll);
        return () => {
            window.removeEventListener("scroll", updateSideNavScroll);
        };
    }, []);
    const onResponsiveMenuChange = (e) => {
        const section = document.querySelector(e.currentTarget.value);
        if (section) {
            const anchor = section.querySelector(".doc_anchor");
            (anchor || section).scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
        }
    };
    const nav = (names) => names.map(name => ({
        name,
        anchor: "#".concat(name
            .replace(/[\W_]+/g, " ")
            .trim()
            .replace(" ", "-")
            .toLowerCase())
    }));
    const NAV_STRUCTURE = [
        { name: "API Documentation", anchor: "#overview" },
        ...nav([
            "Authentication",
            "Content Type",
            "Errors",
            "Generate Audio",
            "Audio Timings"
        ]),
        {
            name: "Speakers and Styles",
            anchor: "#speakers-and-styles",
            subnav: nav(["Available Speakers", "Available Styles"])
        },
        {
            name: "SSML Tags",
            anchor: "#ssml-tags",
            subnav: nav([
                "<speak>",
                "<voicery:speaker>",
                "<voicery:style>",
                "<p>, <s>",
                "<w>, <token>",
                "<say-as>",
                "<break>",
                "<emphasis>",
                "<sub>",
                "<prosody>",
                "<phoneme>",
                "<audio>"
            ])
        }
    ];
    const responsiveNavItem = (item) => (React.createElement("option", { key: item.name, value: item.anchor }, item.name));
    const responsiveNav = NAV_STRUCTURE.map(item => item.subnav ? (React.createElement("optgroup", { key: item.anchor, label: item.name },
        item.name,
        item.subnav.map(subitem => responsiveNavItem(subitem)))) : (responsiveNavItem(item)));
    const standardNavLink = (item) => (React.createElement(HashLink, { to: `/docs${item.anchor}`, scroll: el => {
            const anchor = el.querySelector(".doc_anchor");
            (anchor || el).scrollIntoView({ behavior: "smooth", block: "start" });
        } }, item.name));
    const standardNavItem = (item) => (React.createElement("li", { key: item.anchor }, standardNavLink(item)));
    const standardNav = (React.createElement(Scrollspy, { items: NAV_STRUCTURE.map(item => item.anchor.substring(1)), currentClassName: "active", offset: -200 }, NAV_STRUCTURE.map(item => item.subnav ? (React.createElement("li", { key: item.anchor },
        React.createElement("div", null, standardNavLink(item)),
        React.createElement(Scrollspy, { items: item.subnav.map(subitem => subitem.anchor.substring(1)), currentClassName: "sub-active", offset: -200 }, item.subnav.map(subitem => standardNavItem(subitem))))) : (standardNavItem(item)))));
    return (React.createElement("div", { className: "doc-menu" },
        React.createElement("div", { className: "doc-responsive-menu" },
            React.createElement("select", { id: "nav-responsive", onChange: onResponsiveMenuChange }, responsiveNav)),
        React.createElement("div", { className: "doc-menu-fix" }, standardNav)));
};
const DocsOverview = () => (React.createElement("section", { className: "doc-content", id: "overview" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_title" },
        React.createElement("h2", null, "API Documentation")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "The Voicery API exposes our powerful text-to-speech engine. Our engine supports the standard Speech Synthesis Markup Language (SSML) extended to support speaker styles and emotions. Our API accepts JSON-encoded request bodies and returns either streaming audio or JSON-encoded lines (jsonl). We use standard HTTP verbs and response codes as well as modern standards for header authentication and versioning."),
        React.createElement("p", null,
            "This documentation is intended to provide developers the technical specifications required to integrate the Voicery API. If you\u2019re not a developer, you can login to your",
            " ",
            React.createElement(Link, { className: "doc_link-blue", to: "/dashboard" }, "admin dashboard"),
            " ",
            "to listen and download audio without writing any code."),
        React.createElement("p", { className: "mb-0" }, "The API documentation shows how to use curl to make request from the API. We don\u2019t provide language-specific client libraries, but our documentation applies to the standard http requests library for any programming language. All API endpoints have the same base URL:"),
        React.createElement("div", { className: "doc_code-blocks mb-4" },
            React.createElement(SyntaxHighlighter, { language: "shell", style: github }, getBaseUrl())),
        React.createElement("p", { id: "contact-support" },
            "If ",
            "you're",
            " looking for additional support with the API (questions, issues, customizations, etc.) send an email to",
            " ",
            React.createElement("b", null, "support@voicery.com"),
            " so we can help."))));
const DocsAuthentication = () => (React.createElement("section", { className: "doc-content", id: "authentication" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_title" },
        React.createElement("h2", null, "Authentication")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null,
            "The Voicery API authenticates request using API keys sent in the",
            " ",
            React.createElement("a", { className: "doc_link-blue", href: "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Authorization", rel: "noopener noreferrer", target: "_blank" }, "HTTP Authorization"),
            " ",
            "request header. API keys are sent as bearer tokens (type ",
            '"bearer"',
            ")."),
        React.createElement("p", null,
            "All API requests should be made over",
            " ",
            React.createElement("a", { className: "doc_link-blue", href: "https://en.wikipedia.org/wiki/HTTPS", rel: "noopener noreferrer", target: "_blank" }, "HTTPS"),
            "."),
        React.createElement("p", null,
            "The documentation will use the example fake API key",
            " ",
            React.createElement("b", { className: "doc_green" }, "12345689-0000-1111-2222-012345678917"),
            ", but you can access and manage your production API keys from your",
            " ",
            React.createElement(Link, { className: "doc_link-blue", to: "/dashboard" }, "admin dashboard"),
            "."),
        React.createElement("p", null, "Your authorization header should look like:")),
    React.createElement("div", { className: "doc_code-blocks" },
        React.createElement(SyntaxHighlighter, { language: "shell", style: github }, "Authorization: Bearer 12345689-0000-1111-2222-012345678917"))));
const DocsContentType = () => (React.createElement("section", { className: "doc-content", id: "content-type" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_title" },
        React.createElement("h2", null, "Content Type")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null,
            "All requests are JSON-encoded and versioned using the",
            " ",
            React.createElement("a", { className: "doc_link-blue", href: "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Type", rel: "noopener noreferrer", target: "_blank" }, "HTTP Content-Type"),
            " ",
            "request header. In the header, the encoding is specified as",
            " ",
            React.createElement("b", { className: "doc_green" }, "application/json"),
            " and the version is specified as ",
            React.createElement("b", { className: "doc_green" }, "version=1"),
            " (1 is the current version). The API may change in the future so specifying the version ensures future compatibility with our API endpoints as they evolve."),
        React.createElement("p", null, "Your Content-Type header should look like:")),
    React.createElement("div", { className: "doc_code-blocks" },
        React.createElement(SyntaxHighlighter, { language: "shell", style: github }, "Content-Type: application/json; version=1"))));
const DocsErrors = () => (React.createElement("section", { className: "doc-content", id: "errors" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_title" },
        React.createElement("h2", null, "Errors")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null,
            "Voicery uses standard",
            " ",
            React.createElement("a", { className: "doc_link-blue", href: "https://developer.mozilla.org/en-US/docs/Web/HTTP/Status", rel: "noopener noreferrer", target: "_blank" }, "HTTP response codes"),
            ": ",
            React.createElement("b", { className: "doc_green" }, "2xx"),
            " range indicates success,",
            " ",
            React.createElement("b", { className: "doc_green" }, "4xx"),
            " indicates a client error you need to fix, and ",
            React.createElement("b", { className: "doc_green" }, "5xx"),
            " indicates a server error we need to fix (occurs rarely)."),
        React.createElement("p", null, "Errors are returned with a JSON-encoded response body that provides more details on what went wrong.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "statusCode", type: "integer" }, "The HTTP response code (e.g. 400)."),
        React.createElement(Attribute, { name: "error", type: "string" },
            "The HTTP response code human-readable name (e.g. ",
            '"Bad Request"',
            ")."),
        React.createElement(Attribute, { name: "message", type: "string" }, "A human-readable message indicating what went wrong."))));
const DocsGenerateAudio = () => (React.createElement("section", { className: "doc-content", id: "generate-audio" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_title" },
        React.createElement("h2", null, "Generate Audio")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null,
            "To generate audio, make a ",
            React.createElement("b", { className: "doc_green" }, "POST"),
            " request to the ",
            React.createElement("b", null, "/generate"),
            " endpoint.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "text", type: "string", required: true },
            "The audio request text. If SSML is enabled this must be a valid SSML string. The length of this string is limited to 3000 characters per request. For larger requests we recommend you break them up into chunks and submit them separately. If you have a use case that requires a larger limit please",
            " ",
            React.createElement(HashLink, { className: "doc_link-blue", to: "/docs#contact-support", scroll: el => el.scrollIntoView({ behavior: "smooth", block: "center" }), onClick: () => {
                    const supportLink = document.querySelector("#contact-support");
                    if (supportLink) {
                        setTimeout(() => {
                            supportLink.classList.add("shake");
                        }, 1000);
                        setTimeout(() => {
                            supportLink.classList.remove("shake");
                        }, 3000);
                    }
                } }, "contact us"),
            "."),
        React.createElement(Attribute, { name: "speaker", type: "string", required: true }, "The speaker id of the voice used to generate the audio response."),
        React.createElement(Attribute, { name: "style", type: "string" }, "The style id of the style used to generate the audio response. Styles are only supported on some speakers."),
        React.createElement(Attribute, { name: "encoding", type: "string", defaultValue: "mp3" },
            "The encoding for the audio response. One of",
            " ",
            React.createElement("b", { className: "doc_green" }, "mp3"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "wav"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "pcm"),
            ", or ",
            React.createElement("b", { className: "doc_green" }, "json"),
            ". Using ",
            React.createElement("b", { className: "doc_green" }, "json"),
            " requests",
            " ",
            React.createElement(HashLink, { className: "doc_link-blue", to: "/docs#audio-timings", scroll: el => {
                    const anchor = el.querySelector(".doc_anchor");
                    (anchor || el).scrollIntoView({
                        behavior: "smooth",
                        block: "start"
                    });
                } }, "audio timings"),
            " ",
            "instead of audio."),
        React.createElement(Attribute, { name: "ssml", type: "boolean", defaultValue: "true" },
            "Whether SSML is enabled for this request. SSML request must have valid SSML ",
            React.createElement("b", { className: "doc_green" }, "text")),
        React.createElement(Attribute, { name: "sampleRate", type: "integer", defaultValue: "24000" },
            "The sample rate for the audio response. One of",
            " ",
            React.createElement("b", { className: "doc_green" }, "8000"),
            ",",
            React.createElement("b", { className: "doc_green" }, "16000"),
            ", or",
            " ",
            React.createElement("b", { className: "doc_green" }, "24000"),
            ".")),
    React.createElement(ExampleUsage, { language: "shell" }, `
          curl ${getBaseUrl()}/generate \\
            -H "Authorization: Bearer YOUR-API-KEY" \\
            -H "Content-Type: application/json; version=1" \\
            -d '{"text": "This is a test.", "speaker": "steven", "style": "narration", "encoding": "wav"}' \\
            > my-audio.wav
        `)));
const DocsAudioTimings = () => (React.createElement("section", { className: "doc-content", id: "audio-timings" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_title" },
        React.createElement("h2", null, "Audio Timings")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("div", { style: { marginBottom: "20px" } },
            React.createElement(Alert, { kind: "warning" },
                "Audio Timings is in public beta.",
                React.createElement("br", null),
                "Contact",
                " ",
                React.createElement("span", { style: { textDecoration: "underline" } }, "support@voicery.com"),
                " ",
                "if you have any issues.")),
        React.createElement("p", null,
            "We can provide precise timing information for the sentences, words, and phonemes found in the audio generate from our API. To request audio timings make a standard request to",
            " ",
            React.createElement(HashLink, { className: "doc_link-blue", to: "/docs#generate-audio", scroll: el => {
                    const anchor = el.querySelector(".doc_anchor");
                    (anchor || el).scrollIntoView({
                        behavior: "smooth",
                        block: "start"
                    });
                } }, "generate audio"),
            ", but set the ",
            React.createElement("b", { className: "doc_blue" }, "encoding"),
            " attribute to",
            " ",
            React.createElement("b", { className: "doc_green" }, "json"),
            "."),
        React.createElement("p", null,
            "The timing results are returned as a series of JSON objects in",
            " ",
            React.createElement("a", { className: "doc_link-blue", href: "http://jsonlines.org/", rel: "noopener noreferrer", target: "_blank" }, "JSON-encoded lines"),
            ". We call each of these JSON objects a ",
            React.createElement("b", { className: "doc_blue" }, "token"),
            ".")),
    React.createElement(Attributes, { title: "Token Attributes" },
        React.createElement(Attribute, { name: "type", type: "string", showRequirement: false },
            "The type of this token. One of ",
            React.createElement("b", { className: "doc_green" }, "phoneme"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "word"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "sentence"),
            ", or ",
            React.createElement("b", { className: "doc_green" }, "audio"),
            "."),
        React.createElement(Attribute, { name: "start", type: "integer", showRequirement: false },
            "The index of the start of the text (inclusive) in the original request corresponding to this audio segment. For tokens that do not correspond to text (like silence) this will be ",
            React.createElement("b", { className: "doc_green" }, "null"),
            "."),
        React.createElement(Attribute, { name: "end", type: "integer", showRequirement: false },
            "The index of the end of the text (exclusive) in the original request corresponding to this audio segment. For tokens that do not correspond to text (like silence) this will be ",
            React.createElement("b", { className: "doc_green" }, "null"),
            "."),
        React.createElement(Attribute, { name: "value", type: "string", showRequirement: false },
            "The value of this token. For ",
            React.createElement("b", { className: "doc_green" }, "phoneme"),
            " tokens this is a phoneme from the",
            " ",
            React.createElement("a", { className: "doc_link-blue", href: "http://svn.code.sf.net/p/cmusphinx/code/trunk/cmudict/cmudict.0.7a.phones", rel: "noopener noreferrer", target: "_blank" }, "CMUDict ARPABET subset"),
            " ",
            ". For ",
            React.createElement("b", { className: "doc_green" }, "word"),
            " and",
            " ",
            React.createElement("b", { className: "doc_green" }, "sentence"),
            " tokens this is the text corresponding to this token in the original request. Additionally,",
            " ",
            React.createElement("b", { className: "doc_green" }, "phoneme"),
            " and",
            " ",
            React.createElement("b", { className: "doc_green" }, "word"),
            " tokens can have values of",
            " ",
            React.createElement("b", { className: "doc_green" }, "SIL"),
            " and",
            " ",
            React.createElement("b", { className: "doc_green" }, "<silence>"),
            " (respectively) indicating silence. Tokens of type ",
            React.createElement("b", { className: "doc_green" }, "audio"),
            " will always have value ",
            React.createElement("b", { className: "doc_green" }, "<audio>"),
            "."),
        React.createElement(Attribute, { name: "time", type: "integer", showRequirement: false }, "The time in milliseconds of the start of this token in the audio."),
        React.createElement(Attribute, { name: "duration", type: "integer", showRequirement: false }, "The duration in milliseconds of this token in the audio.")),
    React.createElement(ExampleUsage, { title: "Example Audio Timings", language: "json" }, `
        {"type": "phoneme", "start": null, "end": null, "value": "SIL", "time": 0, "duration": 487}
        {"type": "word", "start": null, "end": null, "value": "<silence>", "time": 0, "duration": 487}
        {"type": "phoneme", "start": 0, "end": 5, "value": "HH", "time": 487, "duration": 50}
        {"type": "phoneme", "start": 0, "end": 5, "value": "EH", "time": 537, "duration": 75}
        {"type": "phoneme", "start": 0, "end": 5, "value": "L", "time": 612, "duration": 100}
        {"type": "phoneme", "start": 0, "end": 5, "value": "OW", "time": 712, "duration": 300}
        {"type": "word", "start": 0, "end": 5, "value": "Hello", "time": 487, "duration": 525}
        {"type": "phoneme", "start": 7, "end": 10, "value": "AY", "time": 1012, "duration": 125}
        {"type": "phoneme", "start": 7, "end": 10, "value": "M", "time": 1137, "duration": 100}
        {"type": "word", "start": 7, "end": 10, "value": "I'm", "time": 1012, "duration": 225}
        {"type": "phoneme", "start": 11, "end": 14, "value": "B", "time": 1237, "duration": 50}
        {"type": "phoneme", "start": 11, "end": 14, "value": "AA", "time": 1287, "duration": 350}
        {"type": "phoneme", "start": 11, "end": 14, "value": "B", "time": 1637, "duration": 125}
        {"type": "word", "start": 11, "end": 14, "value": "Bob", "time": 1237, "duration": 525}
        {"type": "phoneme", "start": null, "end": null, "value": "SIL", "time": 1762, "duration": 450}
        {"type": "word", "start": null, "end": null, "value": "<silence>", "time": 1762, "duration": 450}
        {"type": "sentence", "start": 0, "end": 15, "value": "Hello, I'm Bob!", "time": 0, "duration": 2212}
      `)));
const DocsSpeakersAndStyles = ({ children }) => (React.createElement("section", { className: "doc-content", id: "speakers-and-styles" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_title" },
        React.createElement("h2", null, "Speakers and Styles")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null,
            "If you can\u2019t find what you need or would like a custom voice exclusive to your brand,",
            " ",
            React.createElement(HashLink, { className: "doc_link-blue", to: "/docs#contact-support", scroll: el => el.scrollIntoView({ behavior: "smooth", block: "center" }), onClick: () => {
                    const supportLink = document.querySelector("#contact-support");
                    if (supportLink) {
                        setTimeout(() => {
                            supportLink.classList.add("shake");
                        }, 1000);
                        setTimeout(() => {
                            supportLink.classList.remove("shake");
                        }, 3000);
                    }
                } }, "contact us"),
            ".")),
    React.createElement("div", null, children)));
const DocsAvailableSpeakers = ({ speakers }) => {
    const computeSpeakerRows = () => {
        if (!speakers) {
            return [
                React.createElement("tr", { key: "loading-speakers" },
                    React.createElement("td", { colSpan: 2 },
                        React.createElement(Spinner, { size: 3 })))
            ];
        }
        return speakers.map(speaker => (React.createElement("tr", { key: speaker.id },
            React.createElement("td", null,
                React.createElement("p", null,
                    speaker.name,
                    " ",
                    speaker.source === "vctk" && React.createElement("span", { className: "doc_red" }, "*"),
                    " ",
                    speaker.styles.length > 1 && React.createElement("span", { className: "doc_red" }, "\u2020"),
                    " ",
                    speaker.private && React.createElement("span", { className: "doc_red" }, "\u2021"),
                    " ",
                    React.createElement("span", { className: "doc_green" },
                        "(",
                        speaker.id,
                        ")"))),
            React.createElement("td", null,
                React.createElement("p", null, speaker.lang)))));
    };
    const speakerRows = computeSpeakerRows();
    const hasPrivateSpeakers = speakers
        ? speakers.filter(speaker => speaker.private).length !== 0
        : false;
    return (React.createElement("div", { className: "doc-subcontent", id: "available-speakers" },
        React.createElement("div", { className: "doc_anchor" }),
        React.createElement("div", { className: "doc_sub-title" },
            React.createElement("h4", null, "Available Speakers")),
        React.createElement("div", { className: "doc_info" },
            React.createElement("p", null,
                "Select a speaker by using the listed id as the speaker attribute in the",
                " ",
                React.createElement(HashLink, { smooth: true, className: "doc_link-blue", to: "/docs#generate-audio" }, "generate endpoint"),
                ".")),
        React.createElement("div", { className: "doc_table" },
            React.createElement("table", null,
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("th", null,
                            React.createElement("p", null, "Name (id)")),
                        React.createElement("th", null,
                            React.createElement("p", null, "Description")))),
                React.createElement("tbody", null, speakerRows))),
        React.createElement("div", { className: "doc_info" },
            React.createElement("p", null,
                React.createElement("span", { className: "doc_red" }, "*"),
                " This speaker is from the VCTK dataset provided under the ODC-By license. The audio is lower quality than voices ",
                "we've",
                " collected ourselves."),
            React.createElement("p", null,
                React.createElement("span", { className: "doc_red" }, "\u2020"),
                " This speaker has styles."),
            hasPrivateSpeakers && (React.createElement("p", null,
                React.createElement("span", { className: "doc_red" }, "\u2021"),
                " This speaker is a custom voice that your account has special permissions to access.")))));
};
const DocsAvailableStyles = ({ speakers }) => {
    const computeSpeakerRows = () => {
        if (!speakers) {
            return [
                React.createElement("tr", { key: "loading-speakers" },
                    React.createElement("td", { colSpan: 2 },
                        React.createElement(Spinner, { size: 3 })))
            ];
        }
        return speakers
            .filter(speaker => speaker.styles.length > 1)
            .map(speaker => (React.createElement("tr", { key: speaker.id },
            React.createElement("td", null,
                React.createElement("p", null,
                    speaker.name,
                    " ",
                    React.createElement("span", { className: "doc_green" },
                        "(",
                        speaker.id,
                        ")"))),
            React.createElement("td", null,
                React.createElement("p", null, speaker.styles.map(style => style.name.toLowerCase()).join(", "))))));
    };
    const speakerRows = computeSpeakerRows();
    return (React.createElement("div", { className: "doc-subcontent", id: "available-styles" },
        React.createElement("div", { className: "doc_anchor" }),
        React.createElement("div", { className: "doc_sub-title" },
            React.createElement("h4", null, "Available Styles")),
        React.createElement("div", { className: "doc_info" },
            React.createElement("p", null,
                "Select a style by using both the corresponding speaker and one of the listed style ids as the style attribute in the",
                " ",
                React.createElement(HashLink, { smooth: true, className: "doc_link-blue", to: "/docs#generate-audio" }, "generate endpoint"),
                ".")),
        React.createElement("div", { className: "doc_table" },
            React.createElement("table", null,
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("th", null,
                            React.createElement("p", null, "Name (id)")),
                        React.createElement("th", null,
                            React.createElement("p", null, "Style Ids")))),
                React.createElement("tbody", null, speakerRows)))));
};
const DocsSSMLTags = ({ children }) => (React.createElement("section", { className: "doc-content", id: "ssml-tags" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_title" },
        React.createElement("h2", null, "SSML Tags")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "The root element of the SSML document.")),
    React.createElement("div", null, children)));
const DocsSpeakTag = () => (React.createElement("div", { className: "doc-subcontent", id: "speak" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<speak>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "The root element of the SSML document.")),
    React.createElement(ExampleUsage, { audio: MP3SpeakTag }, `
                    <speak>
                      How are you doing today?
                    </speak>
               `)));
const DocsVoicerySpeakerTag = () => (React.createElement("div", { className: "doc-subcontent", id: "voicery-speaker" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<voicery:speaker>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Adjusts the current speaker of the text.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "name", type: "string", required: true },
            "The id of the speaker to use. Must be one of the",
            " ",
            React.createElement(HashLink, { smooth: true, className: "doc_link-blue", to: "/docs#available-speakers" }, "available speakers")),
        React.createElement(Attribute, { name: "style", type: "string" },
            "The id of the style to use. Must be one of the",
            " ",
            React.createElement(HashLink, { smooth: true, className: "doc_link-blue", to: "/docs#available-styles" }, "available styles"),
            " ",
            "for the given speaker.")),
    React.createElement(ExampleUsage, { audio: MP3VoicerySpeakerTag }, `
        <speak>
          <voicery:speaker name="steven" style="conversational">
            Nice to see you today!
          </voicery:speaker>
          <voicery:speaker name="katie" style="conversational">
            Nice to see you too!
          </voicery:speaker>
        </speak>
     `)));
const DocsVoiceryStyleTag = () => (React.createElement("div", { className: "doc-subcontent", id: "voicery-style" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<voicery:style>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Adjusts the style of the current speaker.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "name", type: "string", required: true },
            "The id of the style to use. Must be one of the",
            " ",
            React.createElement(HashLink, { smooth: true, className: "doc_link-blue", to: "/docs#available-styles" }, "available styles"),
            " ",
            "for the current speaker.")),
    React.createElement(ExampleUsage, { audio: MP3VoiceryStyleTag }, `
        <speak>
           <voicery:speaker name="steven" style="flustered">
             I'm running late.
             <voicery:style name="sad">
               Rats! I missed the bus.
             </voicery:style>
           </voicery:speaker>
        </speak>
      `)));
const DocsPSTag = () => (React.createElement("div", { className: "doc-subcontent", id: "p-s" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<p>, <s>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Provides sentence and paragraphs breaks."),
        React.createElement("p", null, "Sentences are followed by short breaks, while paragraphs are followed by longer breaks.")),
    React.createElement(ExampleUsage, { audio: MP3PSTag }, `
        <speak>
            <p>
                <s>Start with the first sentence.</s>
                <s>Follow with the second sentence.</s>
            </p>
        </speak>
      `)));
const DocsWTokenTag = () => (React.createElement("div", { className: "doc-subcontent", id: "w-token" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<w>, <token>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null,
            "Adjusts the pronunciation of a word by identifying the ",
            "word's",
            " part of speech. The ",
            React.createElement("b", null, "<w>"),
            " and ",
            React.createElement("b", null, "<token>"),
            " tags may be used interchangeably.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "role", type: "string", required: true },
            "The role of token in the sentence. One of:",
            React.createElement("ul", null,
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "voicery:VB"),
                        ": use the (present) verb pronunciation (e.g. wind)")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "voicery:VBD"),
                        ": use the past verb pronunciation (e.g. read),")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "voicery:NN"),
                        ": use the noun pronunciation (e.g. produce),")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "voicery:SENSE_1"),
                        ": use the other pronunciation (e.g. bass)"))))),
    React.createElement(ExampleUsage, { audio: MP3WTokenTag }, `
        <speak>
            Her teacher thought she did not <w role="voicery:VB">read</w> the book,
            but actually she had <w role="voicery:VBD">read</w> it yesterday.
        </speak>
      `)));
const DocsSayAsTag = () => (React.createElement("div", { className: "doc-subcontent", id: "say-as" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<say-as>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Adjust how the text is pronounced by providing additional context.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "interpret-as", type: "string", required: true },
            "How to interpret the text in context. One of:",
            React.createElement("ul", null,
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "cardinal"),
                        ": say as a cardinal (e.g.",
                        " ",
                        '"5" as "five"',
                        ").")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "ordinal"),
                        ": say as a ordinal (e.g.",
                        '"3" as "third"',
                        ").")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "characters"),
                        ": say each character (e.g.",
                        " ",
                        '"abc" as "a b c"',
                        ").")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "unit"),
                        ": say as a unit (e.g.",
                        '"m2" as "meters squared"',
                        ").")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "time"),
                        ": say as a time (e.g.",
                        '"3pm" as "three p m"',
                        ").")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "telephone"),
                        ": say as a telephone number (e.g. ",
                        '"2405551234x823"',
                        ").")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "address"),
                        ": say as an address (e.g.",
                        " ",
                        '"Voicery Ln." as "Voicery Lane"',
                        ").")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "fraction"),
                        ": say as a fraction (e.g.",
                        " ",
                        '"2/3" as"two thirds"',
                        ").")),
                React.createElement("li", null,
                    React.createElement("p", null,
                        React.createElement("b", { className: "doc_green" }, "date"),
                        ": say as a date (e.g.",
                        '"3/15/1993"',
                        ").")))),
        React.createElement(Attribute, { name: "format", type: "string" },
            "Only allowed when interpret-as is ",
            React.createElement("b", { className: "doc_green" }, "date"),
            ". One of ",
            React.createElement("b", { className: "doc_green" }, "mdy"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "dmy"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "ymd"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "md"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "dm "),
            ", ",
            React.createElement("b", { className: "doc_green" }, "ym "),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "my"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "d"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "m"),
            ", or ",
            React.createElement("b", { className: "doc_green" }, "y"),
            ". The characters in the string indicate the ordering of the month (",
            React.createElement("b", { className: "doc_green" }, "m"),
            "), day (",
            React.createElement("b", { className: "doc_green" }, "d"),
            "), and year (",
            React.createElement("b", { className: "doc_green" }, "y"),
            ") of the pronounced date."),
        React.createElement(Attribute, { name: "detail", type: "string" },
            "Only allowed when interpret-as is ",
            React.createElement("b", { className: "doc_green" }, "date"),
            ".",
            " ",
            "Either ",
            React.createElement("b", { className: "doc_green" }, "1"),
            ", indicating that the date should be in form (e.g.",
            '"4/15" as "April 15th"',
            ") or ",
            React.createElement("b", { className: "doc_green" }, "2"),
            ", indicating to pronounce the date in longer (e.g.",
            " ",
            '"4/15" as "the 15th of April"',
            ").")),
    React.createElement(ExampleUsage, { audio: MP3SayAsTag }, `
        <speak>
            On <say-as interpret-as="date" format="md">4/12</say-as>,
            I ran for 25 <say-as interpret-as="unit">mi</say-as>.
        </speak>
      `)));
const DocsBreakTag = () => (React.createElement("div", { className: "doc-subcontent", id: "break" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<break>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Controls silence between words."),
        React.createElement("p", null, "This tag is required to contain no child elements (self-enclosed XML tag).")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "time", type: "string" },
            "The duration of the silence. Specified as a time string in seconds or milliseconds like ",
            React.createElement("b", { className: "doc_green" }, "2s"),
            " or",
            " ",
            React.createElement("b", { className: "doc_green" }, "350ms"),
            " (respectively). Cannot be provided if strength is also provided."),
        React.createElement(Attribute, { name: "strength", type: "string" },
            "The strength of the silence. One of: ",
            React.createElement("b", { className: "doc_green" }, "none"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "x-weak"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "weak"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "medium"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "strong"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "x-strong"),
            ". Cannot be provided if time is also provided.")),
    React.createElement(ExampleUsage, { audio: MP3BreakTag }, `
        <speak>
            Take a moment and listen...
            <break time="350ms" />
            then listen closer...
            <break time="1s" />
            there is just silence.
            <break strength="weak" />
        </speak>
      `)));
const DocsEmphasisTag = () => (React.createElement("div", { className: "doc-subcontent", id: "emphasis" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<emphasis>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Adjusts the emphasis of the selected words.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "level", type: "string" },
            "The amount of emphasis. One of ",
            React.createElement("b", { className: "doc_green" }, "reduced"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "none"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "moderate"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "strong"),
            ". Currently",
            " ",
            React.createElement("b", { className: "doc_green" }, "moderate"),
            "/",
            React.createElement("b", { className: "doc_green" }, "strong"),
            " and",
            " ",
            React.createElement("b", { className: "doc_green" }, "reduced"),
            "/",
            React.createElement("b", { className: "doc_green" }, "none"),
            " ",
            "are treated interchangeably, but this may change in the future.")),
    React.createElement(ExampleUsage, { audio: MP3EmphasisTag }, `
        <speak>
            I <emphasis level="moderate">forgot</emphasis> my homework at home!
        </speak>
      `)));
const DocsSubTag = () => (React.createElement("div", { className: "doc-subcontent", id: "sub" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<sub>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Provides an alternative text used for pronouncing a given word.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "alias", type: "string", required: true }, "The alternate pronunciation.")),
    React.createElement(ExampleUsage, { audio: MP3SubTag }, `
        <speak>
            <sub alias="Carbon dioxide">C02</sub> is a colorless gas.
        </speak>
      `)));
const DocsProsodyTag = () => (React.createElement("div", { className: "doc-subcontent", id: "prosody" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<prosody>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Adjusts the rate, pitch, and/or volume of a block of text.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "rate", type: "string" },
            "Adjusts the audio rate. Either one of",
            " ",
            React.createElement("b", { className: "doc_green" }, "x-slow"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "slow"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "medium"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "fast"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "x-fast"),
            " or a percentage of the original rate between 20% and 110%. We recommend using percentage values instead of string predefined values as these values may change when we increase the maximum rate above 110%."),
        React.createElement(Attribute, { name: "pitch", type: "string" },
            "Adjusts the audio pitch. Either one of",
            " ",
            React.createElement("b", { className: "doc_green" }, "x-low"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "low"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "medium"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "high"),
            ",",
            " ",
            React.createElement("b", { className: "doc_green" }, "x-high"),
            " or a percentage change between -33.3% and +50%."),
        React.createElement(Attribute, { name: "volume", type: "string" },
            "Adjusts the audio volume. Either one of",
            " ",
            React.createElement("b", { className: "doc_green" }, "silent"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "x-soft"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "soft"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "medium"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "loud"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "x-high"),
            " ",
            "or a decibel change at most +4dB (about 1.6 times as loud).")),
    React.createElement(ExampleUsage, { audio: MP3ProsodyTag }, `
        <speak>
            This is my natural voice.
            <prosody pitch="low" rate="105%" volume="-2dB">
                This is my adjusted voice!
            </prosody>
        </speak>
      `)));
const DocsPhonemeTag = () => (React.createElement("div", { className: "doc-subcontent", id: "phoneme" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<phoneme>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Provides a phonetic pronunciation for a given word.")),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "ph", type: "string", required: true }, "The phonetic pronunciation of the word."),
        React.createElement(Attribute, { name: "alphabet", type: "string", defaultValue: "arpabet" },
            "The phonetic alphabet used to specify the phonetic pronunciation. One of",
            " ",
            React.createElement("b", { className: "doc_green" }, "arpabet"),
            ", ",
            React.createElement("b", { className: "doc_green" }, "ipa"),
            ", or ",
            React.createElement("b", { className: "doc_green" }, "x-sampa"),
            ". For",
            " ",
            React.createElement("b", { className: "doc_green" }, "arpabet"),
            " we support the",
            " ",
            React.createElement("a", { className: "doc_link-blue", href: "http://www.speech.cs.cmu.edu/cgi-bin/cmudict", rel: "noopener noreferrer", target: "_blank" }, "CMUDict ARPABET subset"),
            " ",
            "of the general",
            " ",
            React.createElement("a", { className: "doc_link-blue", href: "https://en.wikipedia.org/wiki/ARPABET#Symbols", rel: "noopener noreferrer", target: "_blank" }, "2-letter ARPABET standard"),
            ". See the",
            " ",
            React.createElement("a", { className: "doc_link-blue", href: "https://en.wikipedia.org/wiki/X-SAMPA#Summary", rel: "noopener noreferrer", target: "_blank" }, "X-SAMPA wikipedia page"),
            " ",
            "for an ",
            React.createElement("b", { className: "doc_green" }, "ipa"),
            " and",
            " ",
            React.createElement("b", { className: "doc_green" }, "x-sampa"),
            " reference.")),
    React.createElement(ExampleUsage, { audio: MP3PhonemeTag }, `
        <speak>
            How do you say crayon? Is it pronounced
            <phoneme ph="KREY1AA2N" alphabet="arpabet">crayon</phoneme>,
            <phoneme ph="krˈeɪˌɔn" alphabet="ipa">crayon</phoneme>, or
            <phoneme ph="krA~n" alphabet="x-sampa">crayon</phoneme>?
        </speak>
      `)));
const DocsAudioTag = () => (React.createElement("div", { className: "doc-subcontent", id: "audio" },
    React.createElement("div", { className: "doc_anchor" }),
    React.createElement("div", { className: "doc_sub-title" },
        React.createElement("h4", null, "<audio>")),
    React.createElement("div", { className: "doc_info" },
        React.createElement("p", null, "Includes prerecorded audio or sound effects from external sources."),
        React.createElement("p", null,
            "The provided audio file must abide by the following restrictions. If it does not, the ",
            React.createElement("b", null, "<audio>"),
            " tag is ignored."),
        React.createElement("ul", null,
            React.createElement("li", null,
                React.createElement("p", null, "Sample Rate: Must be 16000 Hz.")),
            React.createElement("li", null,
                React.createElement("p", null, "Format: Either wav or mp3.")),
            React.createElement("li", null,
                React.createElement("p", null, "Bit Depth: When using wav, must have 16 bits per sample.")),
            React.createElement("li", null,
                React.createElement("p", null, "Maximum Size: Must be less than 5 MB. Any audio after 5 MB is dropped.")),
            React.createElement("li", null,
                React.createElement("p", null, "Maximum Length: Must be less than 300 seconds. Any audio after 300 seconds is dropped.")),
            React.createElement("li", null,
                React.createElement("p", null, "Connection Timeout: The server serving the audio must respond in less than three seconds."))),
        React.createElement("p", null, "Using ffmpeg: The following command will generate a valid MP3 file from almost any input audio file."),
        React.createElement("p", null,
            React.createElement("b", null, "ffmpeg -i FILENAME -acodec mp3 -ar 16000 output.mp3"))),
    React.createElement(Attributes, null,
        React.createElement(Attribute, { name: "src", type: "string", required: true },
            "The URL for the external audio. Note that you must properly escape the url to ensure that the input is valid XML. In particular, the character",
            " ",
            React.createElement("b", null, "&"),
            " will need to be replaced by ",
            React.createElement("b", null, "&amp;"),
            " in URLs.")),
    React.createElement(ExampleUsage, { audio: MP3AudioTag }, `
        <speak>
            Are you ready?
            <audio src="https://voicery-public.s3.amazonaws.com/audio/boing.mp3" />
            Let's go!
        </speak>
      `)));
const Docs = () => {
    const sortedSpeakers = SPEAKERS_LIST.sort((s1, s2) => {
        // Sort private voices first.
        if (s1.private !== s2.private) {
            if (s1.private) {
                return -1;
            }
            if (s2.private) {
                return 1;
            }
        }
        // Sort VCTK voices last.
        if (s1.source !== s2.source) {
            if (s1.source === "vctk") {
                return 1;
            }
            if (s2.source === "vctk") {
                return -1;
            }
        }
        // Sort by language.
        if (s1.lang !== s2.lang) {
            if (s1.lang === "English, US") {
                return -1;
            }
            if (s2.lang === "English, US") {
                return 1;
            }
            return s1.lang < s2.lang ? -1 : 1;
        }
        // Then sort by name.
        if (s1.name !== s2.name) {
            return s1.name < s2.name ? -1 : 1;
        }
        return 0;
    });
    return (React.createElement("section", { className: "doc-wrap" },
        React.createElement("div", { className: "container-fluid" },
            React.createElement("div", { className: "row justify-content-md-start justify-content-center" },
                React.createElement("div", { className: "col-md-3 col-12" },
                    React.createElement(DocsNav, null)),
                React.createElement("div", { className: "col-md-8 col-11" },
                    React.createElement("div", { className: "doc_main-container" },
                        React.createElement(Alert, { kind: "warning", className: "pf_input" }, "Voicery shut down in October 2020. This documentation is no longer accurate."),
                        React.createElement(DocsOverview, null),
                        React.createElement(DocsAuthentication, null),
                        React.createElement(DocsContentType, null),
                        React.createElement(DocsErrors, null),
                        React.createElement(DocsGenerateAudio, null),
                        React.createElement(DocsAudioTimings, null),
                        React.createElement(DocsSpeakersAndStyles, null,
                            React.createElement(DocsAvailableSpeakers, { speakers: sortedSpeakers }),
                            React.createElement(DocsAvailableStyles, { speakers: sortedSpeakers })),
                        React.createElement(DocsSSMLTags, null,
                            React.createElement(DocsSpeakTag, null),
                            React.createElement(DocsVoicerySpeakerTag, null),
                            React.createElement(DocsVoiceryStyleTag, null),
                            React.createElement(DocsPSTag, null),
                            React.createElement(DocsWTokenTag, null),
                            React.createElement(DocsSayAsTag, null),
                            React.createElement(DocsBreakTag, null),
                            React.createElement(DocsEmphasisTag, null),
                            React.createElement(DocsSubTag, null),
                            React.createElement(DocsProsodyTag, null),
                            React.createElement(DocsPhonemeTag, null),
                            React.createElement(DocsAudioTag, null))))))));
};
export default Docs;
